/* eslint-disable no-nested-ternary */

// Components
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

// Hooks
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'next/navigation';
import { useMemo } from 'react';
import { TermsSections } from './TermsSections';
import useDayjs from 'hooks/useDayjs';
import useGetCampaignPageCMSData from 'hooks/useGetCampaignPageCMSData';
import useIsMobile from 'hooks/useIsMobile';
import useExcludeAmountOfferingList from 'hooks/useExcludeAmountOfferingList';
import useRegDExcludedList from 'hooks/useRegDExcludedList';
import { getAmountRaised } from 'services/offerings';
import { LEGACY_RESERVATION_LIST } from 'utils/constants/strings';

const TermStats = () => {
  const queryClient = useQueryClient();
  const params = useParams();
  const dayjs = useDayjs();
  const isMobile = useIsMobile();

  // Get offering data from query cache with improved null checking
  const offeringData = queryClient.getQueryData(['offeringData']);

  const { data: EXCLUDE_AMOUNT_OFFERING_LIST, isSuccess: loadedExcluded } =
    useExcludeAmountOfferingList();
  const { data: REGD_EXCLUDED_LIST, isSuccess: loadedRegD } = useRegDExcludedList();
  const { hideTerms } = useGetCampaignPageCMSData({
    slug: offeringData?.slug || params?.slug?.[0],
  });

  const hideAmount = loadedExcluded
    ? EXCLUDE_AMOUNT_OFFERING_LIST.indexOf(offeringData?.slug) > -1
    : true;

  const { data: amountRaisedObject } = useQuery({
    queryKey: ['amountRaised', offeringData?.id],
    queryFn: () => getAmountRaised({ offeringId: offeringData?.id }),
    enabled: Boolean(offeringData?.id) && !LEGACY_RESERVATION_LIST.includes(offeringData?.slug),
    staleTime: 15 * 60 * 1000, // cache for 15 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const termsItems = useMemo(
    () => TermsSections(offeringData, dayjs, hideAmount, amountRaisedObject),
    [offeringData, dayjs, hideAmount, amountRaisedObject],
  );

  // Early return with null check to prevent rendering empty/broken terms sections
  if (!offeringData) {
    return null;
  }

  const regDExclusion = loadedRegD ? !REGD_EXCLUDED_LIST?.includes(offeringData?.slug) : true;

  const isRegD =
    (offeringData?.regulationType === 'Reg D' && regDExclusion) ||
    offeringData?.regulationType === '506(c)';

  const simpleAgreementForFutureEquity = offeringData?.offeringType === 'safe';

  const convertibleNote =
    offeringData?.offeringType === 'convertible_note' || simpleAgreementForFutureEquity;

  if (hideTerms || isRegD) return null;

  return (
    <Grid item container rowSpacing={3} xs={12} position="relative">
      <Grid id="anchor-terms" position="absolute" top="-60px" />
      <Grid item xs={12}>
        <Grid item xs={12}>
          <Typography variant={isMobile ? 'h5Caps' : 'h4'} data-cy="terms-title">
            TERMS
          </Typography>
        </Grid>
        <Typography variant="h7" color="formField.helperText">
          {offeringData?.name}
        </Typography>
      </Grid>
      <Grid item xs={12} mb={-2}>
        <Typography variant="h6" color="secondary.main">
          Overview
        </Typography>
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
        py={0}
        sx={{
          pr: {
            xs: 0,
          },
        }}
      >
        <Grid container direction="column" rowSpacing={3}>
          {convertibleNote ? (
            <>
              {termsItems?.interestRate}
              {simpleAgreementForFutureEquity
                ? !isRegD
                  ? termsItems?.amountRaised
                  : null
                : termsItems?.maturityDate}
              {termsItems?.deadline}
            </>
          ) : (
            <>
              {termsItems?.pricePerShare}
              {termsItems?.deadline}
            </>
          )}
        </Grid>
      </Grid>
      <Grid item xs={12} md={6}>
        <Grid container direction="column" rowSpacing={3}>
          {convertibleNote ? (
            <>
              {termsItems?.valuationCap}
              {simpleAgreementForFutureEquity ? null : termsItems?.fundingGoal}
            </>
          ) : (
            <>
              {termsItems?.valuation}
              {termsItems?.fundingGoal}
            </>
          )}
        </Grid>
      </Grid>
      <Grid item xs={12} mt={3} mb={-2}>
        <Typography variant="h6" color="secondary.main">
          Breakdown
        </Typography>
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          pr: {
            xs: 0,
          },
        }}
      >
        <Grid container direction="column" rowSpacing={3}>
          {convertibleNote ? (
            <>
              {termsItems?.minInvestment}
              {termsItems?.discountRate}
              {termsItems?.conversionTrigger}
            </>
          ) : (
            <>
              {termsItems?.minInvestment}
              {termsItems?.maxInvestment}
              {termsItems?.minNumberShares}
              {termsItems?.maxNumberShares}
            </>
          )}
        </Grid>
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          pr: {
            xs: 0,
          },
        }}
      >
        <Grid container direction="column" rowSpacing={3}>
          {convertibleNote ? (
            <>
              {termsItems?.offeringType}
              {!simpleAgreementForFutureEquity && termsItems?.equityType}
            </>
          ) : (
            <>
              {termsItems?.offeringType}
              {termsItems?.assetType}
              {termsItems?.sharesOffered}
            </>
          )}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        {!convertibleNote && (
          <Typography variant="p3Italicize">
            Maximum Number of Shares Offered subject to adjustment for bonus shares
          </Typography>
        )}
      </Grid>
    </Grid>
  );
};

export default TermStats;
